import type { QueryClient, QueryKey } from "@tanstack/react-query";
import type { AgentFocus } from "@/types/agentFocus.type";
import { brandUniverseChatProposalsKey } from "@/app/routes/dashboard/api/useBrandUniverseChatProposals";
import { brandUniversePendingFactProposalsKey } from "@/app/routes/dashboard/api/useBrandUniversePendingFactProposals";
import { brandMemoriesKey } from "@/app/routes/dashboard/settings/api/useBrandMemories";
import { USER_MEMORIES_ROOT } from "@/app/routes/dashboard/settings/api/useUserMemories";
import {
  marketSignalsKey,
  reconcileSignals,
} from "@/app/routes/dashboard/market-signals/api/signalsCache";
import { sessionAttachmentsKey } from "@/app/routes/dashboard/api/useSessionAttachments";
import { brandMediaKey } from "@/app/routes/dashboard/api/useBrandMedia";
import { planKey, reconcilePlan } from "@/app/routes/dashboard/content-calendar/api/calendarCache";
import {
  planBriefsKey,
  slotBriefKey,
  briefReviewKey,
} from "@/app/routes/dashboard/content-calendar/api/useProductionBrief";
import {
  strategyDetailKey,
  strategyListKey,
} from "@/app/routes/dashboard/strategy/api/useStrategies";

type Ctx = { brandId: string; sessionId: string | null; focus: AgentFocus };

/**
 * Which cached reads a finished tool call invalidates.
 *
 * Every write tool saves straight to the database, so the page the user is
 * looking at is stale the moment one returns. Before the merge each chat panel
 * carried its own callbacks for this and a page could only refresh what it had
 * wired up; one map means a tool refreshes what it touched no matter which page
 * the agent was on when it ran.
 */
const INVALIDATIONS: Record<string, (ctx: Ctx) => QueryKey[]> = {
  // Brand Universe
  ProposeFact: proposalKeys,
  EditProposedFact: proposalKeys,
  DeleteProposedFact: proposalKeys,
  SetSkills: ({ brandId }) => [["brandAgentSessions", brandId]],

  // Brand-wide
  SetMarketSignal: ({ brandId }) => [marketSignalsKey(brandId)],
  SaveMemory: ({ brandId }) => [brandMemoriesKey(brandId)],
  // Personal memory is the user's, not the brand's — one list whatever the brand.
  SavePersonalMemory: () => [USER_MEMORIES_ROOT],
  // A picture saved from a link joins the brand's media library.
  SaveImageFromUrl: ({ brandId }) => [brandMediaKey(brandId)],
  // A video the agent attached from a link joins this chat's attachments.
  AttachVideoFromLink: attachmentKeys,

  // Strategy
  CreateStrategy: strategyKeys,
  WriteStrategyChapter: strategyKeys,
  SetSlideText: strategyKeys,
  SetSlideMedia: strategyKeys,

  // Calendar
  AddPost: calendarKeys,
  UpdatePost: calendarKeys,
  DeletePost: calendarKeys,
  UpdateConcept: calendarKeys,
  WriteConcept: calendarKeys,
  StartMonthPlan: calendarKeys,
  // A post's link lands on its Kit for Client tab, whichever post it was.
  SetPostLink: (ctx) => [...calendarKeys(ctx), ["distribution"]],
  EditBrief: briefKeys,
  // Recovery can touch slots, ideas AND briefs, so it refreshes both.
  RecoverStuckWork: (ctx) => [...calendarKeys(ctx), ...briefKeys(ctx)],
};

function proposalKeys({ brandId, sessionId }: Ctx): QueryKey[] {
  return [
    ...(sessionId ? [brandUniverseChatProposalsKey(brandId, sessionId)] : []),
    brandUniversePendingFactProposalsKey(brandId),
  ];
}

function attachmentKeys({ brandId, sessionId }: Ctx): QueryKey[] {
  return sessionId ? [sessionAttachmentsKey(brandId, sessionId)] : [];
}

function strategyKeys({ brandId, focus }: Ctx): QueryKey[] {
  const strategyId = focus.surface === "strategy" ? focus.strategyId : undefined;
  return [
    ...(strategyId ? [strategyDetailKey(strategyId)] : []),
    strategyListKey(brandId),
  ];
}

function calendarKeys({ brandId, focus }: Ctx): QueryKey[] {
  const planId = focus.surface === "calendar" ? (focus.planId ?? null) : null;
  return [planKey(planId), ["calendarPlans", brandId]];
}

function briefKeys({ focus }: Ctx): QueryKey[] {
  if (focus.surface !== "calendar") return [];
  const planId = focus.planId ?? null;
  return [
    ...(focus.slotId ? [slotBriefKey(focus.slotId)] : []),
    planBriefsKey(planId),
    briefReviewKey(planId),
  ];
}

/** Refetch whatever this tool just changed. No-op for read-only tools. */
export function invalidateForTool(
  queryClient: QueryClient,
  toolName: string,
  ctx: Ctx,
): void {
  const keys = INVALIDATIONS[toolName]?.(ctx);
  if (!keys) return;
  for (const queryKey of keys) invalidate(queryClient, queryKey);
}

/**
 * A month and the signal list go through their write gates, so a tool's refresh
 * never lands on an optimistic change.
 */
function invalidate(queryClient: QueryClient, queryKey: QueryKey) {
  const id = (queryKey[1] as string | null) ?? null;
  if (queryKey[0] === planKey(null)[0]) reconcilePlan(queryClient, id);
  else if (queryKey[0] === marketSignalsKey(null)[0]) reconcileSignals(queryClient, id);
  else void queryClient.invalidateQueries({ queryKey });
}

/**
 * Everything the focused surface could have changed during a turn we did not
 * watch tool-by-tool (a resumed reply after a reload).
 */
export function invalidateForFocus(queryClient: QueryClient, ctx: Ctx): void {
  const keys: QueryKey[] = [
    brandMemoriesKey(ctx.brandId),
    USER_MEMORIES_ROOT,
    ...proposalKeys(ctx),
    ...attachmentKeys(ctx),
    ...(ctx.focus.surface === "strategy" ? strategyKeys(ctx) : []),
    ...(ctx.focus.surface === "calendar" ? [...calendarKeys(ctx), ...briefKeys(ctx)] : []),
  ];
  for (const queryKey of keys) invalidate(queryClient, queryKey);
}
